import { lazy, Suspense, type ElementType } from "react";
import { createHashRouter } from "react-router-dom";

import { AppShellRoute } from "@/app/AppShellRoute";
import { RouteLoadingState } from "@/app/RouteLoadingState";
import { MainnetOnlyGate } from "@/components/MainnetOnlyGate";
import { APP_ROUTE_DEFINITIONS, type AppRouteId } from "@/features/navigation";
import type { OutsideSafeRouteAccess } from "@/features/navigation/appRouteManifest";

type OutsideSafeRouteHandle = {
  outsideSafeReadOnlyAccess: OutsideSafeRouteAccess;
};

function buildOutsideSafeRouteHandle(
  outsideSafeReadOnlyAccess: OutsideSafeRouteAccess
): OutsideSafeRouteHandle {
  return { outsideSafeReadOnlyAccess };
}

const routeComponents: Record<AppRouteId, ElementType> = {
  home: lazy(() => import("@/pages/HomePage").then((module) => ({ default: module.HomePage }))),
  gallery: lazy(() =>
    import("@/pages/GalleryPage").then((module) => ({ default: module.GalleryPage }))
  ),
  delegation: lazy(() =>
    import("@/pages/DelegationPage").then((module) => ({ default: module.DelegationPage }))
  ),
  transfers: lazy(() =>
    import("@/pages/TransfersPage").then((module) => ({ default: module.TransfersPage }))
  ),
  ens: lazy(() => import("@/pages/EnsPage").then((module) => ({ default: module.EnsPage }))),
  settings: lazy(() =>
    import("@/pages/SettingsPage").then((module) => ({ default: module.SettingsPage }))
  )
};

const activityRouteComponent = lazy(() =>
  import("@/pages/ActivityPage").then((module) => ({ default: module.ActivityPage }))
);
const unsupportedNetworkRouteComponent = lazy(() =>
  import("@/pages/UnsupportedNetworkPage").then((module) => ({
    default: module.UnsupportedNetworkPage
  }))
);

function buildRouteElement(Page: ElementType) {
  return (
    <Suspense fallback={<RouteLoadingState />}>
      <Page />
    </Suspense>
  );
}

const operationalRoutes = APP_ROUTE_DEFINITIONS.map((route) => {
  const Page = routeComponents[route.id];
  const routeHandle = buildOutsideSafeRouteHandle(route.outsideSafeReadOnlyAccess);
  if (route.pathSegment === "") {
    return { index: true, element: buildRouteElement(Page), handle: routeHandle };
  }

  return { path: route.pathSegment, element: buildRouteElement(Page), handle: routeHandle };
});

export const router = createHashRouter([
  {
    path: "/",
    element: <AppShellRoute />,
    children: [
      {
        path: "unsupported-network",
        element: buildRouteElement(unsupportedNetworkRouteComponent)
      },
      {
        element: <MainnetOnlyGate />,
        children: [
          ...operationalRoutes,
          {
            path: "activity",
            element: buildRouteElement(activityRouteComponent),
            handle: buildOutsideSafeRouteHandle("allowlisted")
          }
        ]
      }
    ]
  }
]);
